// useHeartbeat.js
// Periodically refreshes the JWT token so the session stays alive as long as
// the dashboard is open.  Runs every INTERVAL_MS milliseconds (default 20 min).
// Silently stores the new token in localStorage.  If the server returns 401
// (token already expired before we got a chance to refresh) it redirects to
// the login page.

import { useEffect, useRef } from 'react';
import config from '../../config';

const INTERVAL_MS = 20 * 60 * 1000; // refresh every 20 minutes

const useHeartbeat = (intervalMs = INTERVAL_MS) => {
  const intervalRef = useRef(null);

  useEffect(() => {
    const refresh = async () => {
      const token = localStorage.getItem('token');
      if (!token) return; // not logged in – nothing to do

      try {
        const res = await fetch(`${config.apiUrl}/auth/refresh`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.status === 401) {
          // Token already fully expired – redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('isAdmin');
          window.location.href = '/login?reason=session_expired';
          return;
        }

        if (res.ok) {
          const data = await res.json();
          if (data.access_token) {
            localStorage.setItem('token', data.access_token);
            if (data.is_admin !== undefined) {
              localStorage.setItem('isAdmin', data.is_admin ? 'true' : 'false');
            }
          }
        }
        // Any other non-ok status (e.g. 500) is silently ignored –
        // we'll try again on the next interval.
      } catch {
        // Network error – silently ignore, try again next interval
      }
    };

    // Run once immediately when the hook mounts, then on every interval
    refresh();
    intervalRef.current = setInterval(refresh, intervalMs);

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [intervalMs]);
};

export default useHeartbeat;
